import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | null} */
  #openAnimationFrame = null;

  /** @type {Promise<void> | null} */
  #closePromise = null;

  #isClosing = false;
  #pageLocked = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.#cancelPendingOpen();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const { dialog } = this.refs;
    if (dialog?.open) dialog.close();
    dialog?.classList.remove('dialog-closing');
    this.#unlockPage();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openAnimationFrame !== null || this.#isClosing) return;

    // A section morph can drop an already-open dialog out of the top layer
    // without unlocking the page. When that happens the body is still locked,
    // so re-promote the dialog synchronously: there are no body writes to batch
    // and a deferred frame would flash the page behind the drawer.
    if (this.#pageLocked) {
      // `showModal()` re-adds `[open]`, which would replay the drawer's
      // slide-in animation and read as a fast close/open. Suppress it for this
      // re-promote only; `#performClose` resets the inline value, so genuine
      // opens and closes still animate normally.
      dialog.style.animation = 'none';
      this.#openModal(dialog);
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = null;

      // A close may have been requested while this frame was pending.
      if (!this.isConnected || dialog.open || this.#isClosing) return;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      this.#pageLocked = true;

      this.#openModal(dialog);
    });
  }

  /**
   * Promotes the dialog to the top layer and wires up dismiss listeners.
   * Assumes the page lock is already in place.
   * @param {HTMLDialogElement} dialog
   */
  #openModal(dialog) {
    try {
      dialog.showModal();
    } catch (error) {
      this.#unlockPage();
      throw error;
    }

    this.dispatchEvent(new DialogOpenEvent());

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Closes the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = () => {
    const { dialog } = this.refs;

    // Closing before the requestAnimationFrame from showDialog runs must cancel
    // that frame; otherwise it can open a "ghost" drawer after this method exits.
    this.#cancelPendingOpen();

    if (this.#closePromise) return this.#closePromise;

    if (!dialog.open) {
      this.#unlockPage();
      return Promise.resolve();
    }

    this.#closePromise = this.#performClose(dialog).finally(() => {
      this.#closePromise = null;
    });

    return this.#closePromise;
  };

  /**
   * Runs one close lifecycle. Concurrent close requests reuse the same promise.
   * @param {HTMLDialogElement} dialog
   */
  async #performClose(dialog) {
    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    try {
      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      // Wait for the closing animation, but never hang: if the `animationend`
      // never fires (interrupted animation, frozen clock, reduced motion, rapid
      // re-open, etc.) the dialog would otherwise stay open with the body locked
      // in `position: fixed`. Race against a safety timeout so close always finishes.
      await Promise.race([
        onAnimationEnd(dialog, undefined, { subtree: false }),
        new Promise((resolve) => setTimeout(resolve, 600)),
      ]);
    } finally {
      // Always leave the native top layer and restore the page, even when the
      // animation helper rejects or the dialog is morphed during an update.
      if (dialog.open) dialog.close();
      dialog.classList.remove('dialog-closing');
      dialog.style.animation = '';
      this.#unlockPage();
      this.#isClosing = false;
    }

    this.dispatchEvent(new DialogCloseEvent());
  }

  #cancelPendingOpen() {
    if (this.#openAnimationFrame === null) return;

    cancelAnimationFrame(this.#openAnimationFrame);
    this.#openAnimationFrame = null;
  }

  #unlockPage() {
    if (!this.#pageLocked) return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#pageLocked = false;
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
